import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Edit, Play, Clock } from "lucide-react";

export function TestDetailsModal({
  open,
  onOpenChange,
  testCase,
  testCaseExecutions,
  getStatusColor,
  onEdit,
  onRun,
  onOpenExecution,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-[#0B0F1A] border-white/10 text-white">
        {testCase && (
          <>
            <DialogHeader>
              <DialogTitle
                className="text-2xl"
                style={{ fontFamily: "Unbounded, sans-serif" }}
              >
                {testCase.name}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                {testCase.description}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              {/* Test Case Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="glass-card p-4 rounded-sm">
                  <p className="text-xs text-slate-500 mb-1">Browser</p>
                  <p className="text-sm font-semibold">{testCase.browser}</p>
                </div>
                <div className="glass-card p-4 rounded-sm">
                  <p className="text-xs text-slate-500 mb-1">Steps</p>
                  <p className="text-sm font-semibold">
                    {testCase.steps.length} steps
                  </p>
                </div>
                <div className="glass-card p-4 rounded-sm">
                  <p className="text-xs text-slate-500 mb-1">Target URL</p>
                  <p className="text-sm font-semibold truncate">
                    {testCase.target_url || "Not specified"}
                  </p>
                </div>
                <div className="glass-card p-4 rounded-sm">
                  <p className="text-xs text-slate-500 mb-1">Created</p>
                  <p className="text-sm font-semibold">
                    {new Date(testCase.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Test Steps */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3"
                  style={{ fontFamily: "Unbounded, sans-serif" }}
                >
                  Test Steps
                </h3>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {testCase.steps.map((step, idx) => (
                      <div key={idx} className="glass-card p-3 rounded-sm">
                        <div className="flex gap-3">
                          <div className="w-6 h-6 bg-[#00E599]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                            <span className="text-[#00E599] text-sm font-bold">
                              {step.step_number}
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm">{step.description}</p>
                            {step.expected_result && (
                              <p className="text-xs text-slate-500 mt-1">
                                Expected: {step.expected_result}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Execution History */}
              <div>
                <h3
                  className="text-lg font-semibold mb-3"
                  style={{ fontFamily: "Unbounded, sans-serif" }}
                >
                  Execution History ({testCaseExecutions.length})
                </h3>
                {testCaseExecutions.length > 0 ? (
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {testCaseExecutions.map((execution, idx) => (
                        <div
                          key={idx}
                          className="glass-card p-3 rounded-sm hover:border-white/20 transition-colors cursor-pointer"
                          onClick={() => onOpenExecution(execution.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Clock className="w-4 h-4 text-[#00E599]" />
                              <div>
                                <p className="text-sm">
                                  {new Date(execution.start_time).toLocaleString()}
                                </p>
                                {execution.end_time && (
                                  <p className="text-xs text-slate-500">
                                    Duration:{" "}
                                    {Math.round(
                                      (new Date(execution.end_time) -
                                        new Date(execution.start_time)) /
                                        1000
                                    )}
                                    s
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge className={getStatusColor(execution.status)}>
                              {execution.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No executions yet
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/5">
                <Button
                  className="flex-1 bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                  onClick={() => onEdit(testCase.id)}
                  data-testid="details-edit-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Test Case
                </Button>
                <Button
                  className="flex-1 bg-[#D946EF] text-white hover:bg-[#C026D3] rounded-sm font-semibold"
                  onClick={() => onRun(testCase.id)}
                  data-testid="details-run-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Run Test
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
